/**
 * Frota · Manutenção · Ordens de serviço — a fila da oficina.
 *
 * Abas por situação com contagem; "Ativas" é a fila de trabalho, ordenada por
 * urgência (atrasada, veículo parado, prioridade, prazo). As O.S. do ERP ficam em
 * /frota/manutencao/erp (consulta por placa) e /frota/manutencao (custo): aqui são
 * as do SIAG, abertas a partir das ocorrências dos checklists ou avulsas.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  MaintenanceCriticalityDto, MaintenanceWorkOrderDto, MaintenanceWorkOrderListDto, MaintenanceWorkOrderStatusDto,
} from "@bi/types";
import { api, ApiError } from "../lib/api";
import { fmtBRL } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { PlateAutocomplete } from "../components/PlateAutocomplete";
import { GroupSelect } from "../features/maintenance/GroupSelect";
import { EmptyState } from "../components/EmptyState";
import { IconPlus, IconWrench } from "../components/icons";
import { CriticalityBadge, WO_STATUS } from "../features/maintenance/labels";
import { useCapabilities } from "../features/maintenance/useCapabilities";
import { fromDateInput, sortWorkQueue } from "../features/maintenance/work-order-ui";

type Aba = "active" | MaintenanceWorkOrderStatusDto;

const ABAS: Array<{ key: Aba; label: string }> = [
  { key: "active", label: "Ativas" },
  { key: "open", label: "Abertas" },
  { key: "scheduled", label: "Agendadas" },
  { key: "in_progress", label: "Em execução" },
  { key: "waiting_parts", label: "Aguardando peças" },
  { key: "completed", label: "Concluídas" },
  { key: "cancelled", label: "Canceladas" },
];

export const TYPE_LABEL = { preventive: "Preventiva", corrective: "Corretiva" } as const;

export function MaintenanceWorkOrdersPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useCapabilities();
  const navigate = useNavigate();
  const [criando, setCriando] = useState(false);

  const aba = (params.get("status") as Aba) || "active";
  const plate = params.get("plate") ?? "";
  const priority = params.get("priority") ?? "";
  const type = params.get("type") ?? "";
  const q = params.get("q") ?? "";
  const overdue = params.get("overdue") === "1";
  const group = params.get("group") ?? "";

  const lista = useQuery({
    queryKey: ["maintenance", "work-orders", aba, plate, priority, type, q, overdue, group],
    queryFn: () => {
      const p = new URLSearchParams({ status: aba, limit: "200" });
      if (plate) p.set("plate", plate);
      if (priority) p.set("priority", priority);
      if (type) p.set("type", type);
      if (q) p.set("q", q);
      if (overdue) p.set("overdue", "1");
      if (group) p.set("group", group);
      return api.get<MaintenanceWorkOrderListDto>(`/maintenance/work-orders?${p}`);
    },
  });

  const setParam = (chave: string, valor: string) => {
    const p = new URLSearchParams(params);
    if (valor) p.set(chave, valor); else p.delete(chave);
    setParams(p, { replace: true });
  };

  const linhas = useMemo(() => {
    const rows = lista.data?.rows ?? [];
    return aba === "active" || overdue ? sortWorkQueue(rows) : rows;
  }, [lista.data, aba, overdue]);
  const counts = lista.data?.counts;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Ordens de Serviço"
        subtitle="Fila da oficina: O.S. abertas a partir das ocorrências dos checklists ou avulsas"
        actions={
          <div className="flex gap-2">
            <Link className="btn-secondary" to="/frota/manutencao/erp">OS do ERP</Link>
            <Link className="btn-secondary" to="/frota/manutencao/nao-conformidades">Não conformidades</Link>
            {can("workorder.manage") && (
              <button className="btn-primary" onClick={() => setCriando((v) => !v)}>
                <IconPlus className="w-4 h-4" /> Nova O.S.
              </button>
            )}
          </div>
        }
      />

      {criando && <NovaOS onCriada={(id) => navigate(`/frota/manutencao/ordens/${id}`)} onFechar={() => setCriando(false)} />}

      <div className="flex gap-2 overflow-x-auto pb-1">
        {ABAS.map((a) => (
          <button
            key={a.key}
            onClick={() => setParam("status", a.key === "active" ? "" : a.key)}
            className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm border ${
              aba === a.key ? "bg-tm-500 text-white border-tm-500" : "bg-white text-gray-600 border-surface-border hover:bg-surface-muted"
            }`}
          >
            {a.label}
            {counts && <span className={`ml-2 text-xs ${aba === a.key ? "text-white/80" : "text-gray-400"}`}>{counts[a.key]}</span>}
          </button>
        ))}
      </div>

      <div className="card p-3 flex flex-wrap gap-2 items-center">
        <PlateAutocomplete value={plate} onSelect={(p) => setParam("plate", p)} />
        <GroupSelect value={group} onChange={(g) => setParam("group", g)} />
        <input className="input w-56" placeholder="Nº da O.S. ou serviço" defaultValue={q}
          onKeyDown={(e) => { if (e.key === "Enter") setParam("q", (e.target as HTMLInputElement).value.trim()); }}
          onBlur={(e) => setParam("q", e.target.value.trim())} />
        <select className="input w-40" value={priority} onChange={(e) => setParam("priority", e.target.value)}>
          <option value="">Toda prioridade</option>
          <option value="high">Alta</option>
          <option value="medium">Média</option>
          <option value="low">Baixa</option>
        </select>
        <select className="input w-40" value={type} onChange={(e) => setParam("type", e.target.value)}>
          <option value="">Todo tipo</option>
          <option value="corrective">Corretiva</option>
          <option value="preventive">Preventiva</option>
        </select>
        <label className={`ml-auto inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border cursor-pointer ${
          overdue ? "bg-danger-50 border-danger-600/30 text-danger-600" : "border-surface-border text-gray-600"
        }`}>
          <input type="checkbox" checked={overdue} onChange={(e) => setParam("overdue", e.target.checked ? "1" : "")} />
          Só atrasadas{counts ? ` (${counts.overdue})` : ""}
        </label>
      </div>

      <div className="card overflow-x-auto">
        {lista.isLoading ? (
          <p className="p-6 text-sm text-gray-500">Carregando…</p>
        ) : linhas.length === 0 ? (
          <EmptyState
            icon={<IconWrench className="w-5 h-5" />}
            title="Nenhuma ordem de serviço aqui"
            description="As O.S. nascem das não conformidades dos checklists (botão “Abrir O.S.”) ou avulsas, em “Nova O.S.”."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2">Nº</th>
                <th className="text-left px-4 py-2">Placa</th>
                <th className="text-left px-4 py-2">Serviço</th>
                <th className="text-left px-4 py-2">Tipo</th>
                <th className="text-left px-4 py-2">Prioridade</th>
                <th className="text-left px-4 py-2">Situação</th>
                <th className="text-left px-4 py-2">Prazo</th>
                <th className="text-right px-4 py-2">Custo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {linhas.map((o) => <Linha key={o.id} o={o} />)}
            </tbody>
          </table>
        )}
      </div>
      {lista.data && lista.data.total > linhas.length && (
        <p className="text-xs text-gray-500">Mostrando {linhas.length} de {lista.data.total}. Refine pelos filtros.</p>
      )}
    </div>
  );
}

function Linha({ o }: { o: MaintenanceWorkOrderDto }) {
  const navigate = useNavigate();
  return (
    <tr className="hover:bg-surface-muted cursor-pointer" onClick={() => navigate(`/frota/manutencao/ordens/${o.id}`)}>
      <td className="px-4 py-3 font-mono text-gray-900">
        <Link to={`/frota/manutencao/ordens/${o.id}`} onClick={(e) => e.stopPropagation()} className="hover:underline">#{o.number}</Link>
      </td>
      <td className="px-4 py-3">
        <span className="font-mono">{o.vehiclePlate}</span>
        {o.vehicleStopped && <span className="badge-red ml-2">Parado</span>}
      </td>
      <td className="px-4 py-3 max-w-[340px]">
        <p className="truncate text-gray-900">{o.description}</p>
        <p className="text-xs text-gray-400">
          {o.executor === "external" ? `Terceiro · ${o.supplierName ?? "—"}` : "Oficina própria"}
          {o.assignee ? ` · ${o.assignee}` : ""}
          {(o.nonConformities?.length ?? 0) > 0 ? ` · ${o.nonConformities!.length} ocorrência(s)` : ""}
        </p>
      </td>
      <td className="px-4 py-3 text-gray-600">{TYPE_LABEL[o.type]}</td>
      <td className="px-4 py-3"><CriticalityBadge value={o.priority as MaintenanceCriticalityDto} /></td>
      <td className="px-4 py-3"><span className={WO_STATUS[o.status].badge}>{WO_STATUS[o.status].label}</span></td>
      <td className={`px-4 py-3 whitespace-nowrap ${o.overdue ? "text-danger-600 font-medium" : "text-gray-600"}`}>
        {o.dueAt ? new Date(o.dueAt).toLocaleDateString("pt-BR") : "—"}
        {o.overdue && <span className="badge-red ml-2">Atrasada</span>}
      </td>
      <td className="px-4 py-3 text-right text-gray-900">{o.cost != null ? fmtBRL(Number(o.cost)) : "—"}</td>
    </tr>
  );
}

/** O.S. avulsa — revisão, serviço sem checklist. A partir de ocorrência, é na tela de não conformidades. */
function NovaOS({ onCriada, onFechar }: { onCriada: (id: string) => void; onFechar: () => void }) {
  const qc = useQueryClient();
  const [placa, setPlaca] = useState("");
  const [descricao, setDescricao] = useState("");
  const [tipo, setTipo] = useState<"corrective" | "preventive">("corrective");
  const [prioridade, setPrioridade] = useState<MaintenanceCriticalityDto>("medium");
  const [prazo, setPrazo] = useState("");
  const [executor, setExecutor] = useState<"internal" | "external">("internal");
  const [fornecedor, setFornecedor] = useState("");
  const [responsavel, setResponsavel] = useState("");
  const [parado, setParado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const sugestoes = useQuery({
    queryKey: ["maintenance", "vehicles", placa],
    queryFn: () => api.get<{ items: Array<{ plate: string; vehicleTypeName: string | null }> }>(`/maintenance/vehicles?q=${encodeURIComponent(placa)}`),
    enabled: placa.length >= 2,
  });

  const criar = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>("/maintenance/work-orders", {
        vehiclePlate: placa,
        description: descricao.trim(),
        type: tipo,
        priority: prioridade,
        dueAt: prazo ? fromDateInput(prazo) : null,
        executor,
        supplierName: executor === "external" ? fornecedor.trim() || null : null,
        assignee: responsavel.trim() || null,
        vehicleStopped: parado,
      }),
    onSuccess: (o) => { qc.invalidateQueries({ queryKey: ["maintenance"] }); onCriada(o.id); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível abrir a O.S."),
  });

  const pode = placa.length >= 5 && descricao.trim().length >= 2 && (executor === "internal" || fornecedor.trim() !== "");

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-gray-900">Nova ordem de serviço</h3>
        <button className="btn-ghost text-sm" onClick={onFechar}>Fechar</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-xs text-gray-500">Placa *</span>
          <input className="input w-full uppercase font-mono mt-0.5" list="os-placas" value={placa}
            onChange={(e) => setPlaca(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} />
          <datalist id="os-placas">
            {(sugestoes.data?.items ?? []).map((v) => <option key={v.plate} value={v.plate}>{v.vehicleTypeName ?? ""}</option>)}
          </datalist>
        </label>
        <label className="block">
          <span className="text-xs text-gray-500">Tipo</span>
          <select className="input w-full mt-0.5" value={tipo} onChange={(e) => setTipo(e.target.value as typeof tipo)}>
            <option value="corrective">Corretiva</option>
            <option value="preventive">Preventiva</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-gray-500">Prioridade</span>
          <select className="input w-full mt-0.5" value={prioridade} onChange={(e) => setPrioridade(e.target.value as MaintenanceCriticalityDto)}>
            <option value="high">Alta</option>
            <option value="medium">Média</option>
            <option value="low">Baixa</option>
          </select>
        </label>
        <label className="block md:col-span-3">
          <span className="text-xs text-gray-500">Serviço *</span>
          <textarea className="input w-full mt-0.5" rows={2} value={descricao} maxLength={2000}
            placeholder="Ex.: Revisão dos 100 mil km" onChange={(e) => setDescricao(e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs text-gray-500">Executor</span>
          <select className="input w-full mt-0.5" value={executor} onChange={(e) => setExecutor(e.target.value as typeof executor)}>
            <option value="internal">Oficina própria</option>
            <option value="external">Terceiro</option>
          </select>
        </label>
        {executor === "external" ? (
          <label className="block">
            <span className="text-xs text-gray-500">Oficina / fornecedor *</span>
            <input className="input w-full mt-0.5" value={fornecedor} maxLength={200} onChange={(e) => setFornecedor(e.target.value)} />
          </label>
        ) : (
          <label className="block">
            <span className="text-xs text-gray-500">Responsável</span>
            <input className="input w-full mt-0.5" value={responsavel} maxLength={200} onChange={(e) => setResponsavel(e.target.value)} />
          </label>
        )}
        <label className="block">
          <span className="text-xs text-gray-500">Prazo</span>
          <input type="date" className="input w-full mt-0.5" value={prazo} onChange={(e) => setPrazo(e.target.value)} />
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={parado} onChange={(e) => setParado(e.target.checked)} />
          Veículo parado aguardando o serviço
        </label>
      </div>
      {erro && <p className="text-sm text-danger-600">{erro}</p>}
      <div className="flex justify-end">
        <button className="btn-primary" disabled={!pode || criar.isPending} onClick={() => criar.mutate()}>
          {criar.isPending ? "Abrindo…" : "Abrir O.S."}
        </button>
      </div>
    </div>
  );
}
